import React from 'react';
import { Hand, Stethoscope, Activity, Sparkles } from 'lucide-react';
import { Card, CardContent, CardDescription } from './ui/card';

const servicios = [
  {
    id: 1,
    titulo: 'Osteopatía',
    descripcion: 'Técnica manual que trata disfunciones de movilidad en articulaciones, músculos, ligamentos y vísceras. Busca el origen del problema para restaurar el equilibrio natural del cuerpo mediante manipulaciones específicas.',
    icono: Stethoscope,
    imagen: 'https://images.unsplash.com/photo-1699523229208-44963df7a087?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDk1Nzh8MHwxfHNlYXJjaHwxfHxvc3Rlb3BhdGh5JTIwdHJlYXRtZW50fGVufDB8fHx8MTc2MTE3MzYxOHww&ixlib=rb-4.1.0&q=85'
  },
  {
    id: 2,
    titulo: 'Acupuntura',
    descripcion: 'Medicina tradicional china que estimula puntos específicos del cuerpo para equilibrar la energía vital. Efectiva para dolor crónico, estrés, insomnio y múltiples desequilibrios funcionales.',
    icono: Sparkles,
    imagen: 'https://images.unsplash.com/photo-1598555763574-dca77e10427e?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2Nzd8MHwxfHNlYXJjaHwxfHxhY3VwdW5jdHVyZSUyMG5lZWRsZXN8ZW58MHx8fHwxNzYxMTczNjI5fDA&ixlib=rb-4.1.0&q=85'
  },
  {
    id: 3,
    titulo: 'Masaje deportivo',
    descripcion: 'Tratamiento especializado para deportistas que previene lesiones, acelera la recuperación muscular y optimiza el rendimiento. Combina técnicas profundas con estiramientos específicos.',
    icono: Activity,
    imagen: 'https://images.unsplash.com/photo-1649751361457-01d3a696c7e6?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2NzB8MHwxfHNlYXJjaHwzfHxzcG9ydHMlMjBtYXNzYWdlfGVufDB8fHx8MTc2MTE3MzYzM3ww&ixlib=rb-4.1.0&q=85'
  },
  {
    id: 4,
    titulo: 'Quiromasaje',
    descripcion: 'Masaje terapéutico que alivia tensiones musculares, mejora la circulación y reduce el estrés. Utiliza maniobras manuales sobre tejidos blandos para restaurar el bienestar físico y emocional.',
    icono: Hand,
    imagen: 'https://images.unsplash.com/photo-1650044252595-cacd425982ff?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2NzF8MHwxfHNlYXJjaHwyfHxjaGlyb3ByYWN0aWMlMjBtYXNzYWdlfGVufDB8fHx8MTc2MTE3MzYzOHww&ixlib=rb-4.1.0&q=85'
  }
];

const ServicesSection = () => {
  return (
    <section id="servicios" className="py-16 md:py-24">
      <div className="container mx-auto px-4">
        <div className="text-center mb-12">
          <h2 className="heading-1 mb-4">Servicios profesionales</h2>
          <p className="body-large max-w-2xl mx-auto">
            Osteópata · Acupuntor · Masajista deportivo · Quiromasajista
          </p>
          <p className="body-medium text-[var(--text-secondary)] mt-2">
            Las sesiones se complementan con ejercicios y automasajes personalizados para cada paciente.
          </p>
        </div>

        <div className="network-grid">
          {servicios.map((servicio) => {
            const IconComponent = servicio.icono;
            return (
              <Card key={servicio.id} className="network-card overflow-hidden" data-testid={`servicio-${servicio.id}`}>
                {/* Imagen de alta calidad */}
                <div className="relative h-56 overflow-hidden">
                  <img 
                    src={servicio.imagen} 
                    alt={servicio.titulo}
                    className="w-full h-full object-cover hover:scale-110 transition-transform duration-500"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/90 via-black/50 to-black/10"></div>
                  <div className="absolute bottom-4 left-4 right-4 flex items-center gap-3">
                    <div className="bg-[var(--brand-dark)] p-2 rounded-xl shadow-lg">
                      <IconComponent size={24} className="text-[var(--brand-primary)]" strokeWidth={2.5} />
                    </div>
                    <span
                      className="text-xl md:text-2xl font-bold text-white"
                      style={{ textShadow: '0 2px 8px rgba(0,0,0,0.8)' }}
                    >
                      {servicio.titulo}
                    </span>
                  </div>
                </div>
                <CardContent className="pt-4">
                  <CardDescription className="network-card-content">
                    {servicio.descripcion}
                  </CardDescription>
                </CardContent>
              </Card>
            );
          })}
        </div>

        {/* About Section con foto */}
        <div className="mt-16 max-w-5xl mx-auto">
          <Card className="network-card overflow-hidden">
            <div className="grid md:grid-cols-5 gap-0">
              {/* Imagen */}
              <div className="md:col-span-2 md:h-auto">
                <img 
                  src="https://customer-assets.emergentagent.com/job_simple-booking-3/artifacts/m2j88ppa_Gemini_Generated_Image_14psw214psw214ps.png"
                  alt="Raúl - Osteópata profesional"
                  className="w-full h-auto md:h-full object-cover object-top"
                />
              </div>
              {/* Texto */}
              <div className="md:col-span-3 p-8 md:p-10 flex flex-col justify-center">
                <h3 className="heading-2 mb-4">Sobre mí</h3>
                <p className="body-medium mb-4 text-[var(--text-secondary)]">
                  Trabajo en la <strong>Escuela Kabat</strong> en Madrid, una institución con más de 30 años de prestigio 
                  en formación de terapeutas manuales.
                </p>
                <p className="body-medium mb-4 text-[var(--text-secondary)]">
                  Tengo <strong>pasión por mi trabajo</strong>. Me encanta ayudar a las personas a superar sus problemas 
                  de salud y enseñar a otros cómo hacerlo.
                </p>
                <p className="body-medium text-[var(--text-secondary)]">
                  La osteopatía puede ayudar a mejorar cualquier tipo de dolencia o desequilibrio del aparato musculoesquelético. 
                  Cada tratamiento es <strong>personalizado</strong> según las necesidades de cada paciente.
                </p>
                <div className="flex flex-wrap gap-3 mt-6">
                  <span className="bg-[var(--brand-primary)] text-[var(--brand-dark)] px-4 py-2 rounded-full text-sm font-medium">
                    +10 años experiencia
                  </span>
                  <span className="bg-[var(--bg-subtle)] text-[var(--brand-dark)] px-4 py-2 rounded-full text-sm font-medium">
                    Formación Escuela Kabat
                  </span>
                  <span className="bg-[var(--bg-subtle)] text-[var(--brand-dark)] px-4 py-2 rounded-full text-sm font-medium inline-flex items-center gap-1">
                    <span className="text-yellow-500">★</span> 4.9 en Google
                  </span>
                </div>
              </div>
            </div>
          </Card>
        </div>
      </div>
    </section>
  );
};

export default ServicesSection;
